import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import {SideDialogComponent} from '../../../../shared/components/side-dialog/side-dialog.component';
import {DialogRef} from '@fundamental-ngx/core/dialog';
import {NotificationsService} from '../../../../service/services/misc/notifications/notifications.service';
import {Utils} from '../../../../core/helpers/Utils';
import {ResourceDataService} from '../../../../service/services/rest/resource-data/resource-data.service';
import {ThemeService} from '../../../../service/services/misc/theme/theme.service';
import {ThemeSubjectModel} from '../../../../service/services/misc/theme/theme-subject.model';
import {Subscription} from 'rxjs';
import {ResourceModel} from '../../../../core/models/resources/resource.model';
import {load} from 'js-yaml';
import {Const, ResourceType} from '../../../../core/helpers/Const';
import {FileUploaderComponent} from '@fundamental-ngx/core/file-uploader';
import {UisService} from "../../../../service/services/misc/uis/uis.service";
import {SwaService} from "../../../../service/services/misc/swa/swa.service";
import {JsonEditorComponent} from "../../../../shared/components/json-editor/json-editor.component";
import {ResourceNotificationService} from "../../../../service/services/misc/fcl/resource-notification.service";
import {DialogService} from "../../../../service/services/misc/dialog/dialog.service";
import {
    CreateGenericResourcesResultComponent
} from "../create-generic-resources-result/create-generic-resources-result.component";
import {
    ConfirmOverwriteYamlContentComponent, FileAction
} from "../confirm-overwrite-yaml-content/confirm-overwrite-yaml-content.component";

export enum ErrorType {
	WrongPath = 'wrongPath',
	InvalidYAML = 'invalidYAML',
	DifferentType = 'differentType',
	DifferentName = 'differentName',
	Other = 'other'
}

interface ValidationError {
	errorMsg: string;
	errorType: ErrorType;
}

interface ResourceIdentifier {
	name: string;
	type: string;
	group: string;
	version: string;
}

interface ResourceResult extends ResourceIdentifier {
	success: boolean;
	errorMessage?: string;
	resource?: ResourceModel;
}


@Component({
    selector: 'app-create-generic-resource-dialog',
    templateUrl: './create-generic-resource-dialog.component.html',
    styleUrls: ['./create-generic-resource-dialog.component.scss'],
    standalone: false
})
export class CreateGenericResourceDialogComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
    public readonly ErrorType = ErrorType;
    public readonly Utils = Utils;
	@ViewChild('dialog') private dialog: SideDialogComponent;
	errorMsg: string;
	errorText: string;
	errorType: ErrorType;
	loading: boolean;
	valid = false;
	theme: string;
	isEdit: boolean;
	validMetadata = true;
	currentPath: string;
	isCollapsed = false;
	isExpanded = false;
	isDialogExpanded = false;

	resource: ResourceModel;
	originalRevision: string;

	uploadedFileNames: string[] = [];
	yamlDocumentCount = 1;
	multiDocumentMode = false;

	@ViewChild(JsonEditorComponent)
	    editor: JsonEditorComponent;

	yamlInputData = '';
	themeSubscription: Subscription;

	@ViewChild('uploadFile')
	    uploadFile: FileUploaderComponent;

	constructor(
		public dialogRef: DialogRef,
		private resourceDataService: ResourceDataService,
		private notificationsService: NotificationsService,
		private themeService: ThemeService,
		private cdRef: ChangeDetectorRef,
		private uisService: UisService,
		private swaService: SwaService,
		private resourceNotificationService: ResourceNotificationService,
		public dialogService: DialogService,
	) {}

	ngOnInit() {
	    this.isEdit = this.dialogRef.data.isEdit;
	    this.currentPath = this.dialogRef.data?.path || this.dialogRef.data?.resource?.path;
	    this.isDialogExpanded = this.dialogRef.data?.isExpanded || false;
	    this.resource = this.dialogRef.data.resource;
	    if (this.resource) {
	        this.originalRevision = this.resource.resource?.metadata?.revision;
	        this.yamlInputData = Utils.convertToYamlFile(this.resource.resource);
	    }
	    this.themeSubscription = this.themeService.themeSubject.asObservable().subscribe((theme: ThemeSubjectModel) => {
	        this.theme = Const.YAML_THEMES[theme.selectedTheme];
	    });

	    this.swaService.sendCustomEvent(
	        this.isEdit? this.swaService.SWA_EVENT_TYPES.OPEN_EDIT_RESOURCE_YAML :
	            this.swaService.SWA_EVENT_TYPES.OPEN_CREATE_RESOURCE_YAML,
	        [this.isEdit ? this.resource?.type : "NA"],
	        []
	    );
	}

	ngAfterViewInit() {
	    if (this.editor) {
	    Utils.initEditor(this.editor.editor, "metadata:...", "spec:...");
	    }
	}
	ngAfterViewChecked() {
	    this.cdRef.detectChanges();
	}

	ngOnDestroy() {
	    if (this.themeSubscription) {
	        this.themeSubscription.unsubscribe();
	    }
	}

	setLoading(isLoading: boolean) {
	    this.loading = isLoading;
	    this.dialogRef.loading(isLoading);
	}

	onSubmit() {
	    this.setLoading(true);

	    try {
	        if (this.yamlInputData.includes('---')) {
	            this.submitMultipleDocuments();
	        } else {
	            let res = Utils.parseYamlToJson(this.yamlInputData);

	            if (this.originalRevision) {
	                res.metadata.revision = this.originalRevision;
	            }
	            this.swaService.sendCustomEvent(
	                this.isEdit? this.swaService.SWA_EVENT_TYPES.EDIT_RESOURCE_WITH_YAML :
	                    this.swaService.SWA_EVENT_TYPES.CREATE_RESOURCE_WITH_YAML,
	                [res?.type],
	                []
	            );

	            let resourceDataMethod = this.isEdit
	                ? this.resourceDataService.update('', '', {parameters: res})
	                : this.resourceDataService.add('', '', {parameters: res});
	            resourceDataMethod
	                .subscribe({
	                    next: this.onSuccess.bind(this),
	                    error: this.onError.bind(this, res)
	                })
	                .add(() => {
	                    this.setLoading(false);
	                });
	        }
	    } catch (e: any) {
	        this.setLoading(false);
	    }
	}

	submitMultipleDocuments() {
	    const yamlDocs = this.yamlInputData.split(/^---$/m).filter(doc => doc.trim());
	    const originalDocs = [...yamlDocs];

	    const documents = yamlDocs.map(doc => this.parseYamlSafe(doc)).filter(doc => doc !== null);

	    if (documents.length === 0) {
	        this.showError('No valid YAML documents found', ErrorType.InvalidYAML);
	        return;
	    }

	    const progress = {
	        completed: 0,
	        total: documents.length
	    };

	    const results: ResourceResult[] = [];
	    const successfulResources: ResourceModel[] = [];
	    const successfulIndices: number[] = [];

	    documents.forEach((doc, index) => {
	        const resourceId = this.createResourceId(doc, index);
	        this.processDocument(doc, index, resourceId, results, successfulResources, successfulIndices, progress, originalDocs);
	    });
	}

	private parseYamlSafe(doc: string): any | null {
	    try {
	        return Utils.parseYamlToJson(doc);
	    } catch (e) {
	        return null;
	    }
	}

	private createResourceId(doc: any, index: number): ResourceIdentifier {
	    return {
	        name: doc.metadata?.name || `unknown-${index}`,
	        type: doc.type || 'unknown',
	        group: doc.apiVersion?.split('/')[0] || 'unknown',
	        version: doc.apiVersion?.split('/')[1] || 'unknown'
	    };
	}

	private processDocument(doc: any, index: number, resourceId: ResourceIdentifier, results: ResourceResult[],
	    successfulResources: ResourceModel[], successfulIndices: number[],
	    progress: {completed: number, total: number}, originalDocs: string[]) {
	    if (this.isEdit && index === 0 && this.originalRevision) {
	        doc.metadata.revision = this.originalRevision;
	    }

	    const resourceDataMethod = this.isEdit
	        ? this.resourceDataService.update('', '', { parameters: doc })
	        : this.resourceDataService.add('', '', { parameters: doc });

	    resourceDataMethod.subscribe({
	        next: (res) => {
	            successfulResources.push(res);
	            successfulIndices.push(index);

	            const parentPath = this.dialogRef.data?.path;
	            this.resourceNotificationService.resourceCreated(
					{path: parentPath} as ResourceModel,
					res
	            );

	            results.push({ ...resourceId, success: true, resource: res });
	            progress.completed++;
	            this.checkCompletion(results, successfulResources, successfulIndices, progress, originalDocs);
	        },
	        error: (error) => {
	            results.push({ ...resourceId, success: false, errorMessage: error.message });
	            progress.completed++;
	            this.checkCompletion(results, successfulResources, successfulIndices, progress, originalDocs);
	        }
	    });
	}

	private checkCompletion(results: ResourceResult[], successfulResources: ResourceModel[],
	    successfulIndices: number[], progress: {completed: number, total: number},
	    originalDocs: string[]) {
	    if (progress.completed === progress.total) {
	        const successful = results.filter(r => r.success);
	        const failed = results.filter(r => !r.success);

	        if (failed.length > 0) {
	            this.handleFailedDocuments(failed, successful, originalDocs, successfulIndices);
	        } else {
	            this.handleSuccessCompletion(successfulResources);
	        }

	        this.setLoading(false);
	    }
	}

	private handleFailedDocuments(failed: ResourceResult[], successful: ResourceResult[], originalDocs: string[], successfulIndices: number[]) {
	    if (successful.length > 0) {
	        const failedDocs = originalDocs.filter((_, idx) => !successfulIndices.includes(idx));
	        this.updateEditorWithFailedDocs(failedDocs);
	    }

	    const errorMessages = failed.map(f =>
	        `${f.name} (${f.type}, ${f.group}/${f.version}): ${f.errorMessage}`
	    );

	    this.showError(errorMessages.join('\n'), ErrorType.Other);
	    this.dialogService.openDialog(CreateGenericResourcesResultComponent, { successful, failed });
	}

	private updateEditorWithFailedDocs(failedDocs: string[]) {
	    this.yamlInputData = failedDocs.join('\n---\n');
	    setTimeout(() => {
	        if (this.editor) {
	            this.editor.setText(this.yamlInputData);
	            this.setEditorState();
	        }
	    }, 0);
	}

	private handleSuccessCompletion(successfulResources: ResourceModel[]) {
	    const message = 'Creating resources…';
	    this.notificationsService.showMessageToast(message);

	    if (successfulResources.length === 1) {
	        this.dialogRef.close(successfulResources[0]);
	    } else {
	        this.dialogRef.close(successfulResources);
	    }
	}

	private showError(message: string, type: ErrorType) {
	    this.errorMsg = message;
	    this.errorType = type;
	    this.setLoading(false);
	}

	onError(res: any, error: Error) {
	    this.errorMsg = error.message;

	    this.swaService.sendCustomEvent((this.isEdit ? this.swaService.SWA_EVENT_TYPES.EDIT_RESOURCE_WITH_YAML_FAILED :
	        this.swaService.SWA_EVENT_TYPES.CREATE_RESOURCE_WITH_YAML_FAILED) + " - " + res.type, [this.errorMsg], []);

	    this.setLoading(false);
	    this.cdRef.detectChanges();
	    this.dialog.focusErrorStrip();
	}

	handleFileSelection(files: File[]): void {
	    if (!files || files.length === 0) return;

	    const existingContent = this.yamlInputData.trim();

	    const newFileNames = Array.from(files).map(file => file.name);
	    const selectedFiles = Array.from(files);

	    if (existingContent) {
	        this.dialogService.openDialog(ConfirmOverwriteYamlContentComponent, {
	            fileCount: files.length
	        }).afterClosed.subscribe(result => {
	            if (result === FileAction.OVERWRITE) {
	                this.yamlInputData = '';
	                this.uploadedFileNames = [...newFileNames];
	                this.processFiles(selectedFiles, '');
	            } else if (result === FileAction.APPEND) {
	                this.uploadedFileNames = [...(this.uploadedFileNames || []), ...newFileNames];
	                this.processFiles(selectedFiles, existingContent);
	            } else {
	                console.log('File upload canceled');
	            }
	            this.uploadFile?.clear();
	        });
	    } else {
	        this.uploadedFileNames = [...newFileNames];
	        this.processFiles(selectedFiles, '');
	        this.uploadFile?.clear();
	    }
	}

	processFiles(files: File[], existingContent: string): void {
	    if (files.length > 1 || (files.length === 1 && existingContent)) {
	        this.multiDocumentMode = true;
	        this.processAndAppendFiles(files, existingContent);
	    } else if (files.length === 1 && !existingContent) {
	        const file = files[0];
	        Utils.parseGenericYamlFile(file, (jsonRes: string) => {
	            this.yamlInputData = `# ${file.name}\n${jsonRes}`;

	            setTimeout(() => {
	                if (this.editor) {
	                    this.editor.setText(this.yamlInputData);
	                    this.setEditorState();
	                }
	            }, 0);

	            this.validateMetadata();
	        }).catch((error) => {
	            if (error === 'YAML_WITH_MORE_THAN_ONE_RESOURCE') {
	                this.errorMsg =
						'Reason: YAML specifies more than one resource. This option is limited to one resource per YAML configuration.';
	            } else {
	                this.errorMsg = 'Reason: File does not contain valid YAML syntax or structure.';
	                this.errorType = ErrorType.InvalidYAML;
	            }
	            this.validMetadata = false;
	        });
	    }
	}

	async processAndAppendFiles(files: File[], existingContent: string) {
	    try {
	        this.setLoading(true);
	        const fileContents = await Promise.all(
	            Array.from(files).map(async (file) => {
	                const content = await file.text();
	                return `# ${file.name}\n${content}`;
	            })
	        );

	        let newContent = '';
	        if (existingContent) {
	            newContent = existingContent;
	        }

	        fileContents.forEach((content) => {
	            if (newContent && newContent.trim()) {
	                newContent += '\n---\n';
	            }
	            newContent += content;
	        });
	        this.yamlInputData = newContent;

	        setTimeout(() => {
	            if (this.editor) {
	                this.editor.setText(this.yamlInputData);
	                this.editor.onEditorTextChange();
	                this.setEditorState();
	            }
	        }, 0);
	        this.validateMultiDocumentYaml();

	    } catch (error: any) {
	        this.errorMsg = 'Error processing files: ' + error.message;
	        this.errorType = ErrorType.InvalidYAML;
	        this.validMetadata = false;
	    } finally {
	        this.setLoading(false);
	    }
	}

	validateMultiDocumentYaml(): void {
	    try {
	        const yamlDocs = this.yamlInputData.split(/^---$/m)
	            .filter(doc => doc.trim());

	        let isValid = true;
	        let errorMsgs: string[] = [];
	        let hasPathError = false;
	        yamlDocs.forEach((docContent, index) => {
	            try {
	                const yaml = this.parseYaml(docContent);
	                const currentPath = this.currentPath;
	                const validationResult = this.getValidationErrorMsg(this.isEdit, yaml, currentPath);

	                if (validationResult) {
	                    isValid = false;
	                    const docName = this.uploadedFileNames[index] || `Document ${index + 1}`;
	                    errorMsgs.push(`${docName}: ${validationResult.errorMsg}`);
	                    if (validationResult.errorType === ErrorType.WrongPath) {
	                        hasPathError = true;
	                    }
	                }
	            } catch (e: any) {
	                isValid = false;
	                const docName = this.uploadedFileNames[index] || `Document ${index + 1}`;
	                errorMsgs.push(`${docName}: Invalid YAML syntax - ${e.message}`);
	            }
	        });

	        this.validMetadata = isValid;
	        this.errorMsg = errorMsgs.join('\n');
	        this.errorType = hasPathError ? ErrorType.WrongPath :
	            (errorMsgs.length > 0 ? ErrorType.InvalidYAML : null);

	    } catch (e) {
	        this.errorMsg = 'Invalid YAML document structure';
	        this.errorType = ErrorType.InvalidYAML;
	        this.validMetadata = false;
	    }
	}

	validateMetadata(): void {
	    if (this.multiDocumentMode) {
	        this.validateMultiDocumentYaml();
	    } else {
	        try {
	            const yaml = this.parseYaml(this.yamlInputData) || {};
	            const currentPath = this.dialogRef.data?.path || this.dialogRef.data?.resource?.path;
	            const validationResult = this.getValidationErrorMsg(this.isEdit, yaml, currentPath);
	            this.validMetadata = !validationResult;
	            this.errorMsg = validationResult?.errorMsg;
	            this.errorType = validationResult?.errorType;
	        } catch (e) {
	            this.errorMsg = 'Reason: File does not contain valid YAML syntax or structure.';
	            this.errorType = ErrorType.InvalidYAML;
	            this.validMetadata = false;
	        }
	    }
	    this.errorText = this.getValidationErrorText(this.isEdit, this.errorType);
	}

	getValidationErrorMsg(isEdit: boolean, yaml: any, currentPath: string): ValidationError | undefined {
	    if ([ResourceType.FOLDER, ResourceType.RESOURCE_GROUP].includes(yaml.type)) {
	        return {
	            errorMsg: 'Reason: This tool does not allow the creation of folders and resource groups by YAML configuration. Use instead the dedicated options in this tool for creating these resource types.',
	            errorType: ErrorType.Other
	        };
	    }

	    if (currentPath !== yaml.metadata?.path) {
	        return {
	            errorMsg: this.getWrongPathMsg(),
	            errorType: ErrorType.WrongPath
	        };
	    }

	    if (isEdit && this.resource) {
	        if (this.resource.name !== yaml.metadata?.name) {
	            return {
	                errorMsg: "Reason: metadata.name is different. You cannot change a resource's technical name.",
	                errorType: ErrorType.DifferentName
	            };
	        }
	        if (this.resource.type !== yaml.type) {
	            return {
	                errorMsg: "Reason: The provided value in 'type' does not match the type of the resource.",
	                errorType: ErrorType.DifferentType
	            };
	        }
	    }

	    return undefined;
	}
	getValidationErrorText(isEdit: boolean, errorType: ErrorType) {
	    if (errorType === ErrorType.InvalidYAML || errorType === ErrorType.DifferentType) {
	        return $localize`:XMSG|Error in yaml title@@organizations.dialogs.create-generic-resource-dialog.yamlErrorTitle:Error in YAML`;
	    } else if (errorType === ErrorType.WrongPath) {
	        return undefined;
	    }

	    return isEdit ? 'Cannot edit resource' : 'Cannot create resource';
	}
	private getWrongPathMsg() {
	    return  $localize`:XMSG|Error in yaml title@@organizations.dialogs.create-generic-resource-dialog.pathErrorTitle:Error In YAML.\nReason: The provided value in 'metadata.path' does not match the path of the resource.`;
	}

	onSuccess(res: any) {
	    const msg = (this.isEdit ? 'Updating resource ' : 'Creating resource ') + res.name + ' (' + res.type + ')...';
	    this.notificationsService.showMessageToast(msg);
	    this.dialogRef.close(res);

	    this.swaService.sendCustomEvent((this.isEdit ? this.swaService.SWA_EVENT_TYPES.EDIT_RESOURCE_WITH_YAML_SUCCESS :
	        this.swaService.SWA_EVENT_TYPES.CREATE_RESOURCE_WITH_YAML_SUCCESS), [res.type], []);
	}

	onTextChange(event: string) {
	    this.validateMetadata();
	}

	validateForm() {
	    return this.valid && this.validMetadata;
	}

	clearEditor() {
	    this.uploadFile?.clear();
	    this.uploadFile?.setInputValue([]);
	    this.yamlInputData = '';
	    this.valid = false;
	}

	parseYaml(code: any) {
	    try {
	        return load(code) as any;
	    } catch (e: any) {
	        throw e;
	    }
	}

	copyPath() {
	    this.notificationsService.showMessageToast('Current path copied to clipboard');
	    Utils.copyToClipboard(this.currentPath);
	    this.swaService.sendCustomEvent(this.swaService.SWA_EVENT_TYPES.COPY_PATH, [this.currentPath], []);
	    this.uisService.sendUISEvent(this.uisService.UIS_EVENT_TYPES.COPY_PATH, {path: this.currentPath});
	}

	onCollapseYaml() {
	    Utils.setEditorDefaultState("metadata:...", "spec:...", this.editor.editor.getSession(), true);
	    this.isCollapsed = true;
	    this.isExpanded = false;
	}

	onExpandYaml() {
	    this.editor.setExpand();
	    this.isCollapsed = false;
	    this.isExpanded = true;
	}
	onToggleDialogExpand() {
	    this.isDialogExpanded = !this.isDialogExpanded;
	    this.dialogRef.toggleFullScreen();

	}
	setEditorState() {
	    if (this.isCollapsed) {
		    this.onCollapseYaml();
	    } else if (this.isExpanded) {
		    this.onExpandYaml();
	    } else {
		    Utils.initEditor(this.editor.editor, "metadata:...", "spec:...");
	    }
	}
	onFixPathClick() {
	    try {
	        const yamlDocs = this.yamlInputData.split(/^---$/m)
	            .filter(doc => doc.trim());

	        const fixedDocs = yamlDocs.map(docContent => {
	            try {
	                const yamlObject = this.parseYaml(docContent) || {};
	                const updatedYaml = typeof yamlObject === 'string' || yamlObject instanceof String
	                    ? { [yamlObject as string]: "" }
	                    : yamlObject;
	                updatedYaml.metadata = updatedYaml.metadata || {};
	                updatedYaml.metadata.path = this.currentPath;
	                return Utils.convertToYamlFile(updatedYaml);
	            } catch (e) {
	                return docContent;
	            }
	        });
	        this.yamlInputData = fixedDocs.join('\n---\n');

	        setTimeout(() => {
	            if (this.editor) {
	                this.editor.setText(this.yamlInputData);
	                this.setEditorState();
	            }
	            this.validateMetadata();
	        }, 0);
	    } catch (e) {
	        console.error('Error fixing path(s):', e);
	        // Do nothing
	    }
	}

	onDocumentCountChange(count: number): void {
	    this.yamlDocumentCount = count;
	    this.multiDocumentMode = count > 1;
	}
}
